import { useMemo } from "react";
import type { DocumentItem } from "@/types/ui-models";
import { NoRealData } from "@/components/shared";
import DocumentStatusBadge from "./DocumentStatusBadge";
import { hasStatus, isExpired } from "../lib/docUtils";

interface ProjectSourcesPanelProps {
  documents: DocumentItem[];
  onViewDocument: (id: string) => void;
}

interface ProjectGroup {
  id: string;
  name: string;
  docs: DocumentItem[];
}

export default function ProjectSourcesPanel({ documents, onViewDocument }: ProjectSourcesPanelProps) {
  const groups = useMemo<ProjectGroup[]>(() => {
    const map = new Map<string, ProjectGroup>();
    for (const doc of documents) {
      if (!doc.projectId) continue;
      const g = map.get(doc.projectId) ?? { id: doc.projectId, name: doc.projectName || `Projeto ${doc.projectId}`, docs: [] };
      g.docs.push(doc);
      map.set(doc.projectId, g);
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }, [documents]);

  if (groups.length === 0) {
    return (
      <NoRealData
        title="Nenhum documento vinculado a projeto"
        description="Quando houver documentos cadastrados com projeto informado, eles aparecem aqui agrupados por projeto."
        icon="ri-folder-open-line"
      />
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-foreground-900">Fontes documentais por projeto</h3>

      {groups.map((project) => {
        const totalDocs = project.docs.length;
        const pendingDocs = project.docs.filter((d) => hasStatus(d, "em revisão", "rascunho")).length;
        const expiredDocs = project.docs.filter(isExpired).length;
        const validatedDocs = project.docs.filter((d) => hasStatus(d, "validado")).length;

        return (
          <div key={project.id} className="bg-white rounded-xl border border-background-200/70 p-4 md:p-5">
            <h4 className="text-base font-semibold text-foreground-900 font-heading mb-4">{project.name}</h4>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              <div className="bg-background-50 rounded-lg p-2.5 text-center">
                <div className="text-lg font-bold text-foreground-900 font-heading">{totalDocs}</div>
                <div className="text-[10px] text-foreground-500">Total docs</div>
              </div>
              <div className="bg-primary-50 rounded-lg p-2.5 text-center">
                <div className="text-lg font-bold text-primary-700 font-heading">{validatedDocs}</div>
                <div className="text-[10px] text-primary-600">Validados</div>
              </div>
              <div className="bg-warning-50 rounded-lg p-2.5 text-center">
                <div className="text-lg font-bold text-warning-700 font-heading">{pendingDocs}</div>
                <div className="text-[10px] text-warning-600">Pendentes</div>
              </div>
              <div className="bg-danger-50 rounded-lg p-2.5 text-center">
                <div className="text-lg font-bold text-danger-700 font-heading">{expiredDocs}</div>
                <div className="text-[10px] text-danger-600">Vencidos</div>
              </div>
            </div>

            <div className="space-y-1.5">
              {project.docs.slice(0, 5).map((doc) => (
                <div key={doc.id} className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-background-50 transition-colors">
                  <div className="flex items-center gap-2 min-w-0">
                    <i className="ri-file-text-line text-foreground-400 w-4 h-4 flex items-center justify-center shrink-0" />
                    <button onClick={() => onViewDocument(doc.id)} className="text-sm text-foreground-700 hover:text-accent-600 transition-colors truncate text-left">{doc.title}</button>
                  </div>
                  <DocumentStatusBadge status={doc.status} />
                </div>
              ))}
              {project.docs.length > 5 && (
                <div className="text-xs text-foreground-400 text-center py-1">+ {project.docs.length - 5} documentos</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
